/**
 * The SECOND call this application makes to `client-hub-api`, and the first authenticated
 * one.
 *
 * **Attention, `fetch` again rather than Apollo, and that is a deliberate reading of the note
 * on `activateAccount`.** That note says the day a second call lands is the moment to bring
 * Apollo in. Its own reasoning is what argues against doing so here: a provider wrapped
 * around the whole application, private routes included, plus a codegen configuration to
 * maintain, for a request that needs neither cache nor normalisation. This one asks for a
 * single boolean, on one screen, and wants it FRESH every time. The threshold moves to the
 * third call, and it moves for a real reason: two hand-written queries are two, three are a
 * client.
 *
 * **No cache, on purpose.** The requirement changes in the back-office while the person is
 * signed in; a cached « not required » would let somebody through for the rest of their
 * session on a policy that has since been set.
 */
import { graphql } from './client';

/** What the gate needs to know, and nothing more. */
export interface MfaRequirement {
    /** At least one of the person's accounts requires MFA. */
    required: boolean;
}

const QUERY = `
    query ViewerMfaRequirement {
        viewer {
            mfaRequired
        }
    }
`;

/**
 * Reads the effective requirement of the signed-in person.
 *
 * **Attention, IT FAILS OPEN, and the choice deserves its sentence.** On a network failure,
 * a missing configuration or a null `viewer`, it answers « not required » and the person
 * goes on to their space. Failing closed would put somebody in front of a wall on a
 * transient error, and that wall would sit in front of `/account`, the very screen where a
 * factor is registered: the gate would then block the fix for the thing it demands.
 *
 * What that costs is bounded and self-correcting: the demand is not lost, it is deferred to
 * the next load. And it costs nothing in security terms that is not already true, this
 * requirement being an onboarding control rather than a boundary as long as no factor is
 * registered. Once one is, Cognito asks for the code at every sign-in whatever this
 * function answers.
 *
 * **`viewer` being null is the ordinary case**, not an error: it means the person has no row
 * in our user table, which is what most authenticated visitors of this ecosystem are. No
 * account, so no account requiring anything.
 */
export const fetchMfaRequirement = async (idToken: string): Promise<MfaRequirement> => {
    try {
        const payload = await graphql<{ viewer?: { mfaRequired?: boolean | null } | null }>(QUERY, undefined, idToken);
        if (payload.errors?.length) {
            return { required: false };
        }
        return { required: payload.data?.viewer?.mfaRequired === true };
    } catch {
        return { required: false };
    }
};
